import {
  MessageSquarePlus,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DropMenu } from '~/components/dropmenu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  deleteAgentChatSession,
  renameAgentChatSession,
} from '~/repositories/agentChatSessionRepository';
import { cn } from '~/utils';
import {
  DEFAULT_AGENT_SESSION_TITLE_I18N_KEY,
  useAgentSessionHeader,
} from './agentSessionHeaderContext';

type AgentSessionHeaderProps = {
  className?: string;
};

export const AgentSessionHeader = ({
  className,
}: AgentSessionHeaderProps): ReactElement | null => {
  const { t } = useTranslation('studio');
  const { sessionTitle, setSessionTitle, handlers } = useAgentSessionHeader();
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  if (!handlers) {
    return null;
  }

  const { sessionId, workspaceId, onNewChat, onTitleChange } = handlers;
  const displayTitle =
    sessionTitle.trim() || t(DEFAULT_AGENT_SESSION_TITLE_I18N_KEY);

  const openRename = (): void => {
    setRenameValue(displayTitle);
    setRenameError(null);
    setRenameOpen(true);
  };

  const closeRename = (): void => {
    setRenameOpen(false);
    setRenameValue('');
    setRenameError(null);
    setRenameSaving(false);
  };

  const submitRename = async (): Promise<void> => {
    const nextTitle = renameValue.trim();
    if (!nextTitle) {
      setRenameError(t('agentUi.titleRequired'));
      return;
    }
    if (!workspaceId || !sessionId) {
      setSessionTitle(nextTitle);
      onTitleChange(nextTitle);
      closeRename();
      return;
    }

    setRenameSaving(true);
    setRenameError(null);
    try {
      const updated = await renameAgentChatSession(
        workspaceId,
        sessionId,
        nextTitle
      );
      const savedTitle = updated.title?.trim() || nextTitle;
      setSessionTitle(savedTitle);
      onTitleChange(savedTitle);
      closeRename();
    } catch {
      setRenameError(t('agentUi.renameFailed'));
      setRenameSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (workspaceId && sessionId) {
      try {
        await deleteAgentChatSession(workspaceId, sessionId);
      } catch {
        // Continue navigation even if delete fails silently.
      }
    }
    onNewChat();
  };

  const handleNewChat = (): void => {
    onNewChat();
  };

  return (
    <>
      <div
        className={cn('flex min-w-0 flex-1 items-center gap-1.5', className)}
      >
        <h1 className="min-w-0 truncate text-sm font-semibold text-foreground sm:text-base">
          {displayTitle}
        </h1>

        <DropMenu
          items={[
            {
              id: 'new-chat',
              label: (
                <>
                  <MessageSquarePlus aria-hidden="true" className="size-4" />
                  {t('agentUi.newChat')}
                </>
              ),
              onSelect: handleNewChat,
            },
            {
              id: 'rename',
              label: (
                <>
                  <Pencil aria-hidden="true" className="size-4" />
                  {t('agentUi.rename')}
                </>
              ),
              onSelect: openRename,
            },
            {
              destructive: true,
              id: 'delete',
              label: (
                <>
                  <Trash2 aria-hidden="true" className="size-4" />
                  {t('agentUi.delete')}
                </>
              ),
              onSelect: () => void handleDelete(),
              separatorBefore: true,
            },
          ]}
          trigger={
            <button
              aria-label={t('agentUi.chatActions')}
              className="grid size-8 shrink-0 touch-manipulation place-items-center rounded-lg text-muted transition hover:bg-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
              type="button"
            >
              <MoreHorizontal aria-hidden="true" className="size-4" />
            </button>
          }
        />
      </div>

      <Dialog
        onOpenChange={open => {
          if (!open) {
            closeRename();
          }
        }}
        open={renameOpen}
      >
        <DialogContent className="sm:max-w-md" showCloseButton>
          <DialogHeader>
            <DialogTitle>{t('agentUi.renameChat')}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            onChange={event => setRenameValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void submitRename();
              }
            }}
            placeholder={t('agentUi.chatTitle')}
            value={renameValue}
          />
          {renameError ? (
            <p className="text-sm text-destructive">{renameError}</p>
          ) : null}
          <DialogFooter>
            <Button
              disabled={renameSaving}
              onClick={closeRename}
              type="button"
              variant="outline"
            >
              {t('agentUi.cancel')}
            </Button>
            <Button
              disabled={renameSaving}
              onClick={() => void submitRename()}
              type="button"
            >
              {t('agentUi.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
